import React, { useState, useContext, useEffect } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "context/Auth";
import { useAuth } from "hooks/useAuth";
import capitalize from "../../utils/helpers/capitalize";
import {
  Alert,
  Box,
  Typography,
  Skeleton,
  Button,
  AlertTitle,
  Link,
} from "@mui/material";
import { LocalHospitalRounded, PersonRounded } from "@mui/icons-material";
import InfoBox from "components/common/InfoBox";
import AddressSettings from "./AddressSettings";
import AccountSettings from "./AccountSettings";
import PhoneSettings from "./PhoneSettings";
import MarketingSettings from "./advertising/MarketingSettings";
import UsageDisplay from "./account/UsageDisplay";
import PlanManagement from "./account/PlanManagement";
import { LocationContext } from "context/Location";
import { formatDistanceToNow } from "date-fns";

const Settings = () => {
  const { user, userData, subscription } = useContext(AuthContext);
  const {
    hasActiveTrial,
    hasActiveDailyPass,
    hasValidSubscription,
    subscriptionTier,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { location } = useContext(LocationContext);

  const [loading, setLoading] = useState();
  const [submitted, setSubmitted] = useState();

  const [addressOpen, setAddressOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);

  // Handle URL parameters for auto-opening dialogs
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "phone") {
      setPhoneOpen(true);
      // Clear the parameter to avoid reopening on refresh
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const sendToPortal = async (loadingKey = "portal") => {
    setLoading(loadingKey);

    const functionRef = firebase
      .app()
      .functions("us-central1")
      .httpsCallable("ext-firestore-stripe-payments-createPortalLink");
    const { data } = await functionRef({
      returnUrl: window.location.href,
    });
    window.location.assign(data.url);
  };

  return (
    <Box>
      <Typography
        variant="h4"
        component="h2"
        sx={{ mt: { xs: 1, sm: 5 }, mb: 2 }}
      >
        Settings
      </Typography>

      <Typography sx={{ mb: 4 }}>
        You're logged in as {user?.email}
        {userData?.role === "facility" && location?.title ? (
          <>
            {" "}
            using the <strong>{capitalize(location.title)}</strong> facility
            account.
          </>
        ) : (
          <>.</>
        )}
      </Typography>

      {submitted ? (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSubmitted(null)}
        >
          {submitted}
        </Alert>
      ) : null}

      {/* Daily Pass Status Display */}
      {hasActiveDailyPass && userData?.dailyPassExpiresAt && (
        <Alert severity="info" sx={{ pt: 1, mb: 3 }}>
          <Box>
            <AlertTitle>Daily Pass Active</AlertTitle>
            Your {userData?.subscriptionTier || "Medium"} Daily Pass expires{" "}
            {formatDistanceToNow(new Date(userData.dailyPassExpiresAt), {
              addSuffix: true,
            })}
            .
          </Box>
        </Alert>
      )}

      {/* Trial Status Display */}
      {hasActiveTrial && (
        <Alert severity="success" sx={{ pt: 1, mb: 3 }}>
          <Box>
            <AlertTitle>Free Trial Active.</AlertTitle>
            Enjoying Medical SuperIntelligence? Upgrade now to continue
            unlimited access after your trial ends.
            <Button
              color="inherit"
              variant="outlined"
              onClick={() => navigate("/dashboard/upgrade")}
              sx={{ mt: 2, mb: 1, display: "block" }}
            >
              Upgrade
            </Button>
          </Box>
        </Alert>
      )}

      {/* Usage Display */}
      {userData?.role !== "facility" && (
        <UsageDisplay userData={userData} subscription={subscription} />
      )}

      {/* Plan Management for non-facilities - Only show for users with REAL subscription (not daily pass or trial) */}
      {hasValidSubscription &&
        !hasActiveTrial &&
        !hasActiveDailyPass &&
        userData?.role !== "facility" && (
          <PlanManagement
            subscriptionTier={subscriptionTier}
            loading={loading}
            onPortal={sendToPortal}
            onChangePlan={() => navigate("/dashboard/upgrade")}
          />
        )}

      {/* Facility Info */}
      {userData?.role === "facility" && (
        <>
          <InfoBox
            icon={
              <LocalHospitalRounded
                fontSize="large"
                sx={{ color: "#117aca" }}
              />
            }
            title="Your Facility"
            description="Manage your facility location, group settings for multi-location maps, and advertising profile."
            links={[
              {
                title: "Manage Facility & Location",
                onClick: () => setAddressOpen(true),
              },
              {
                title: "Update Your Advertising Profile",
                onClick: () => setMarketingOpen(true),
              },
            ]}
          />

          {/* Multi-Location Map URL Alert - Only show if group name is set */}
          {location?.group && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Patients can view only your {location.group} facilities at{" "}
              <Link
                href={`${window.location.origin}/map/${location.group
                  .toLowerCase()
                  .replace(/[^a-z0-9\s\-'&]/g, "")
                  .replace(/[\s'&]+/g, "-")
                  .replace(/-+/g, "-")
                  .replace(/^-|-$/g, "")}`}
                target="_blank"
                variant="body2"
              >
                {window.location.origin}/map/
                {location.group
                  .toLowerCase()
                  .replace(/[^a-z0-9\s\-'&]/g, "")
                  .replace(/[\s'&]+/g, "-")
                  .replace(/-+/g, "-")
                  .replace(/^-|-$/g, "")}
              </Link>
              .
            </Alert>
          )}

          {/* Plan Management for facilities - Only show for users with REAL subscription (not daily pass or trial) */}
          {hasValidSubscription && !hasActiveTrial && !hasActiveDailyPass && (
            <PlanManagement
              subscriptionTier={subscriptionTier}
              loading={loading}
              onPortal={sendToPortal}
              onChangePlan={() => navigate("/dashboard/upgrade")}
            />
          )}
        </>
      )}

      {/* Account Management */}
      <InfoBox
        icon={<PersonRounded fontSize="large" color="primary" />}
        title="Your Account"
        description="Manage your account settings and contact information."
        links={[
          {
            title: "Update Email",
            onClick: () => setAccountOpen(true),
          },
          {
            title: userData?.phoneVerified
              ? "Update Phone Number"
              : "Verify Phone Number",
            onClick: () => setPhoneOpen(true),
          },
        ]}
      />

      {/* Settings Dialogs */}
      <AddressSettings
        user={user}
        data={userData}
        location={location}
        visible={addressOpen}
        close={() => setAddressOpen(false)}
        setSubmitted={setSubmitted}
      />

      <MarketingSettings
        user={user}
        data={userData}
        location={location}
        visible={marketingOpen}
        close={() => setMarketingOpen(false)}
        setSubmitted={setSubmitted}
      />

      <AccountSettings
        user={user}
        visible={accountOpen}
        close={() => setAccountOpen(false)}
        setSubmitted={setSubmitted}
      />

      <PhoneSettings
        user={user}
        userData={userData}
        visible={phoneOpen}
        close={() => setPhoneOpen(false)}
        setSubmitted={setSubmitted}
      />
    </Box>
  );
};

export default Settings;
